import { CustomContentProps } from 'notistack';
import { forwardRef } from 'react';

import { buttonClasses, Stack, Typography, useTheme } from '@mui/material';
import { BlurredStack } from 'modules/common/components/BlurredStack';

/**
 * Custom notification component.
 */
export const Notification = forwardRef<
  HTMLDivElement,
  CustomContentProps & {
    isCompactOnMobile?: boolean;
  }
>(({ isCompactOnMobile, message, header, action: _action, id }, ref) => {
  const action = typeof _action === 'function' ? _action(id) : _action;
  const theme = useTheme();

  return (
    <BlurredStack
      direction="row"
      justifyContent="space-between"
      role="alert"
      ref={ref}
      sx={{
        color: 'textSecondary',
        width: {
          xs: undefined,
          sm: 420,
        },
        p: {
          xs: theme.spacing(1.5),
          sm: theme.spacing(2.5),
        },
        gap: {
          xs: 0.5,
          sm: 1.5,
        },
        borderRadius: theme.spacing(1),
        position: 'relative',
        // border: '1px solid',
        borderColor: theme => theme.palette.grey[50],
        boxShadow: `0px 4px 6px -2px #10182808, 0px 12px 16px -4px #10182814`,
      }}
    >
      <Stack gap={0.5} paddingRight={0.8}>
        {header && (
          <Typography
            variant="h7"
            component="div"
            sx={{
              color: 'textSecondary',
              marginBottom: theme.spacing(0.5),
              fontSize: isCompactOnMobile
                ? {
                    xs: theme.typography.pxToRem(16),
                    sm: theme.typography.pxToRem(20),
                  }
                : theme.typography.pxToRem(20),
              mb: 0,
            }}
          >
            {header}
          </Typography>
        )}
        {message && (
          <Typography
            variant="body1"
            sx={{
              color: 'textSecondary',
              display: isCompactOnMobile
                ? {
                    xs: 'none',
                    sm: 'block',
                  }
                : undefined,
            }}
          >
            {message}
          </Typography>
        )}
      </Stack>

      {action ? (
        <Stack
          sx={{
            [`.${buttonClasses.root}`]: {
              textWrap: 'nowrap',
            },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {action}
        </Stack>
      ) : null}
    </BlurredStack>
  );
});

Notification.displayName = 'Notification';
